/* eslint-disable i18next/no-literal-string */
import { useEffect, useRef, useState } from "react";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import indexCss from "~/index.css?raw";

type ThemeToken = { name: string };

const COLOR_VALUE_PATTERN =
  /^(?:#|(?:oklch|oklab|rgb|rgba|hsl|hsla|lab|lch|color-mix)\(|var\(--[\w-]+\)$)/i;

const GROUP_ORDER = [
  "Base",
  "Card",
  "Popover",
  "Primary",
  "Secondary",
  "Muted",
  "Accent",
  "Destructive / Status",
  "Border / Input / Ring",
  "Charts",
  "Sidebar",
  "Tags",
  "Custom",
] as const;

function normalizeValue(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function isColorValue(value?: string) {
  return value ? COLOR_VALUE_PATTERN.test(value) : false;
}

function parseThemeTokens(cssSource: string) {
  const tokens = new Set<string>();
  const blockPattern = /(^|\n)\s*(:root|\.dark)\s*\{([\s\S]*?)\n\}/g;
  const declarationPattern = /--([\w-]+)\s*:\s*([^;]+);/g;

  for (const block of cssSource.matchAll(blockPattern)) {
    const body = block[3];

    if (!body) continue;

    for (const declaration of body.matchAll(declarationPattern)) {
      const name = declaration[1];
      const rawValue = declaration[2];

      if (!name || !rawValue) continue;

      const value = normalizeValue(rawValue);
      if (isColorValue(value)) tokens.add(name);
    }
  }

  return [...tokens]
    .map((name) => ({ name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function getGroupLabel(name: string) {
  if (name === "background" || name === "foreground") return "Base";
  if (name === "card" || name.startsWith("card-")) return "Card";
  if (name === "popover" || name.startsWith("popover-")) return "Popover";
  if (name === "primary" || name.startsWith("primary-")) return "Primary";
  if (name === "secondary" || name.startsWith("secondary-")) return "Secondary";
  if (name === "muted" || name.startsWith("muted-")) return "Muted";
  if (name === "accent" || name.startsWith("accent-")) return "Accent";
  if (
    ["destructive", "success", "warning", "info"].some(
      (prefix) => name === prefix || name.startsWith(`${prefix}-`),
    )
  ) {
    return "Destructive / Status";
  }
  if (["border", "input", "ring"].includes(name))
    return "Border / Input / Ring";
  if (name.startsWith("chart-")) return "Charts";
  if (name === "sidebar" || name.startsWith("sidebar-")) return "Sidebar";
  if (name === "tag" || name.startsWith("tag-")) return "Tags";

  return "Custom";
}

function groupTokens(tokens: ThemeToken[]) {
  const groups = new Map<string, ThemeToken[]>();

  for (const token of tokens) {
    const label = getGroupLabel(token.name);
    const current = groups.get(label) ?? [];
    current.push(token);
    groups.set(label, current);
  }

  return GROUP_ORDER.map((label) => ({
    label,
    tokens: groups.get(label) ?? [],
  })).filter((group) => group.tokens.length > 0);
}

const colorGroups = groupTokens(parseThemeTokens(indexCss));

function Swatch({ tokenName }: { tokenName: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState("");

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    setValue(
      getComputedStyle(element).getPropertyValue(`--${tokenName}`).trim(),
    );
  }, [tokenName]);

  return (
    <div ref={ref} className="space-y-3 rounded-xl p-3">
      <div className="flex justify-end gap-3 text-xs">
        <span className="text-muted-foreground truncate">
          {value || "Inherited"}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="border-border h-20 w-full rounded-lg border"
          style={{ backgroundColor: `var(--${tokenName})` }}
        />
      </div>
    </div>
  );
}

function ButtonShowcase() {
  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle>Active theme preview</CardTitle>
        <CardDescription>
          Buttons rendered with the active theme tokens.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <Button variant="default">Default</Button>
        <Button variant="outline">Outline</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="destructive">Destructive</Button>
        <Button variant="link" className="justify-start px-0">
          Link
        </Button>
      </CardContent>
    </Card>
  );
}

function PaletteContent() {
  return (
    <div className="flex flex-col items-center gap-8">
      {colorGroups.map((group) => (
        <section key={group.label} className="w-full max-w-2xl space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">{group.label}</h3>
            <p className="text-muted-foreground text-sm">
              {group.tokens.length} token{group.tokens.length === 1 ? "" : "s"}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {group.tokens.map((token) => (
              <Card key={token.name} className="gap-4">
                <CardHeader>
                  <CardTitle className="font-mono text-sm">{`--${token.name}`}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Swatch tokenName={token.name} />
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}

      <section className="space-y-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Buttons</h3>
          <p className="text-muted-foreground text-sm">
            Component-level verification against the same token set.
          </p>
        </div>

        <div className="grid gap-4">
          <ButtonShowcase />
        </div>
      </section>
    </div>
  );
}

export default function ColorPalettePage() {
  const darkColumnRef = useRef<HTMLDivElement>(null);

  // While this page is mounted, strip `.dark` from <html> so the left column
  // renders a clean light theme. The right column re-applies `.dark` locally.
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    darkColumnRef.current?.classList.add("dark");
    return () => {
      if (wasDark) root.classList.add("dark");
    };
  }, []);

  return (
    <div className="grid min-h-[calc(100vh-4rem)] md:grid-cols-2">
      <div className="bg-background text-foreground flex flex-col gap-6 p-6">
        <h2 className="text-xl font-semibold">Light</h2>
        <PaletteContent />
      </div>
      <div
        ref={darkColumnRef}
        className="bg-background text-foreground flex flex-col gap-6 p-6"
      >
        <h2 className="text-xl font-semibold">Dark</h2>
        <PaletteContent />
      </div>
    </div>
  );
}
